import { useState, useEffect, useCallback, useMemo } from "react";
import {
  useListConversations,
  useDeleteConversation,
  useUpdateConversation,
} from "@via/api/ai-insight-conversations.api";
import type { Conversation } from "@via/utils/interfaces";
import type { Message } from "common";

interface UseConversationsOptions {
  enabled?: boolean; // Whether to load conversations from API
  page?: number;
  pageSize?: number;
  searchKey?: string;
  pinnedOnly?: boolean;
}

interface UseConversationsReturn {
  conversations: Map<string, Conversation>;
  activeConversationId: string | null;
  activeConversation: Conversation | null;
  setActiveConversationId: (id: string | null) => void;
  createNewConversation: () => string; // Returns temporary conversationId
  updateConversationHistory: (
    id: string,
    history: Array<{ role: "user" | "assistant" | "system"; content: string }>
  ) => void;
  appendUserMessage: (id: string, content: string) => void;
  appendAssistantMessage: (id: string, content: string) => void;
  appendMessage: (id: string, message: Message) => void;
  clearConversationHistory: (id: string) => void;
  updateConversationMeta: (
    id: string,
    payload: Partial<
      Pick<
        Conversation,
        | "conversationHistory"
        | "title"
        | "contextFiles"
        | "isPinned"
        | "pageUrl"
      >
    >
  ) => Promise<void>;
  replaceConversationId: (tempId: string, realId: string) => void; // Replace temp ID with real ID (preserving latest history)
  deleteConversation: (id: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  total: number;
  pagedConversations: Conversation[];
}

/**
 * Hook for managing multiple AI Insight conversations
 *
 * Responsibilities:
 * - Manage conversations state as Map<conversationId, Conversation>
 * - Track active conversation ID
 * - Load conversations from API (filtered by context)
 * - Create new conversation (local state, no DB until first question)
 * - Switch active conversation
 * - Update conversation history
 * - Track pending conversations (not yet saved to DB)
 * - Replace temporary IDs with real IDs when backend creates conversation
 */
export function useConversations({
  enabled = true,
  page = 0,
  pageSize = 10,
  searchKey = "",
  pinnedOnly = false,
}: UseConversationsOptions): UseConversationsReturn {
  // Local state: Map of all conversations (both pending and saved)
  const [conversations, setConversations] = useState<Map<string, Conversation>>(
    new Map()
  );
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);

  // Load conversations from API (paginated)
  const {
    data: apiPage,
    isLoading: isLoadingFromApi,
    error: apiError,
  } = useListConversations({
    page,
    pageSize,
    searchKey,
    pinnedOnly,
    enabled,
  });

  const deleteConversationMutation = useDeleteConversation();
  const updateConversationMutation = useUpdateConversation();

  // Load conversations from API into local state
  useEffect(() => {
    if (!enabled || !apiPage) return;

    const pageConversations = apiPage.data;

    setConversations(prev => {
      const newMap = new Map(prev);

      // Update or add conversations from API page
      pageConversations.forEach(conv => {
        newMap.set(conv.id, conv);
      });

      // Keep pending conversations (those with temp- prefix) that aren't in API response
      // This allows pending conversations to persist until they're saved
      prev.forEach((conv, id) => {
        if (id.startsWith("temp-") && !newMap.has(id)) {
          newMap.set(id, conv);
        }
      });

      return newMap;
    });
  }, [apiPage, enabled]);

  // Do NOT automatically set first conversation as active
  // User must explicitly select conversations from the list
  // Only pending (temp) conversations can be auto-selected if they're the only ones
  useEffect(() => {
    if (activeConversationId === null && conversations.size > 0) {
      // Only auto-select if there are ONLY pending conversations (newly created, not from DB)
      const pendingConversations = Array.from(conversations.values()).filter(
        conv => conv.isPending
      );
      if (
        pendingConversations.length > 0 &&
        pendingConversations.length === conversations.size
      ) {
        // All conversations are pending, select the most recent one
        const sorted = pendingConversations.sort(
          (a, b) => b.updatedAt - a.updatedAt
        );
        setActiveConversationId(sorted[0].id);
      }
      // If there are DB conversations, don't auto-select - user must choose from list
    }
  }, [conversations, activeConversationId]);

  // Get active conversation
  const activeConversation = useMemo(() => {
    if (!activeConversationId) return null;
    return conversations.get(activeConversationId) || null;
  }, [conversations, activeConversationId]);

  // Create new conversation (local state only, marked as pending)
  // Note: contextRoutePattern and contextName should be provided when calling this
  // They will be set when the first question is sent via socket
  const createNewConversation = useCallback((): string => {
    const tempId = `temp-${crypto.randomUUID()}`;
    const newConversation: Conversation = {
      id: tempId,
      title: null,
      conversationHistory: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      contextRoutePattern: "", // Will be set when first question is sent
      contextName: "", // Will be set when first question is sent
      isPending: true,
    };

    setConversations(prev => {
      const newMap = new Map(prev);
      newMap.set(tempId, newConversation);
      return newMap;
    });

    setActiveConversationId(tempId);
    return tempId;
  }, []);

  // Update conversation history
  const updateConversationHistory = useCallback(
    (
      id: string,
      history: Array<{ role: "user" | "assistant" | "system"; content: string }>
    ) => {
      setConversations(prev => {
        let conv = prev.get(id);

        // If conversation does not exist yet (edge case), create a pending shell
        if (!conv) {
          const now = Date.now();
          conv = {
            id,
            title: null,
            conversationHistory: [],
            createdAt: now,
            updatedAt: now,
            contextRoutePattern: "",
            contextName: "",
            isPending: true,
          };
        }

        const newMap = new Map(prev);
        newMap.set(id, {
          ...conv,
          conversationHistory: history,
          updatedAt: Date.now(),
        });
        return newMap;
      });
    },
    []
  );

  // Append a single user message to a conversation
  const appendUserMessage = useCallback((id: string, content: string) => {
    setConversations(prev => {
      let conv = prev.get(id);

      // If conversation does not exist yet, create a pending shell
      if (!conv) {
        const now = Date.now();
        conv = {
          id,
          title: null,
          conversationHistory: [],
          createdAt: now,
          updatedAt: now,
          contextRoutePattern: "",
          contextName: "",
          isPending: true,
        };
      }

      const newMap = new Map(prev);
      newMap.set(id, {
        ...conv,
        conversationHistory: [
          ...conv.conversationHistory,
          { role: "user", content },
        ],
        updatedAt: Date.now(),
      });
      return newMap;
    });
  }, []);

  // Append a single assistant message to a conversation
  const appendAssistantMessage = useCallback((id: string, content: string) => {
    setConversations(prev => {
      let conv = prev.get(id);

      // If conversation does not exist yet, create a pending shell
      if (!conv) {
        const now = Date.now();
        conv = {
          id,
          title: null,
          conversationHistory: [],
          createdAt: now,
          updatedAt: now,
          contextRoutePattern: "",
          contextName: "",
          isPending: true,
        };
      }

      const newMap = new Map(prev);
      newMap.set(id, {
        ...conv,
        conversationHistory: [
          ...conv.conversationHistory,
          { role: "assistant", content },
        ],
        updatedAt: Date.now(),
      });
      return newMap;
    });
  }, []);

  const appendMessage = useCallback((id: string, message: Message) => {
    setConversations(prev => {
      let conv = prev.get(id);

      if (!conv) {
        const now = Date.now();
        conv = {
          id,
          title: null,
          conversationHistory: [],
          createdAt: now,
          updatedAt: now,
          contextRoutePattern: "",
          contextName: "",
          isPending: true,
        };
      }

      const entry = {
        role: message.role as "user" | "assistant" | "system",
        content: message.content,
        mcpToolCall: message.mcpToolCall,
      };

      const newMap = new Map(prev);
      newMap.set(id, {
        ...conv,
        conversationHistory: [...conv.conversationHistory, entry],
        updatedAt: Date.now(),
      });
      return newMap;
    });
  }, []);

  // Clear all messages for a conversation
  const clearConversationHistory = useCallback((id: string) => {
    setConversations(prev => {
      const conv = prev.get(id);
      if (!conv) return prev;

      const newMap = new Map(prev);
      newMap.set(id, {
        ...conv,
        conversationHistory: [],
        updatedAt: Date.now(),
      });
      return newMap;
    });
  }, []);

  // Replace temporary ID with real ID (when backend creates conversation)
  const replaceConversationId = useCallback(
    (tempId: string, realId: string) => {
      setConversations(prev => {
        const tempConversation = prev.get(tempId);
        if (!tempConversation) {
          return prev;
        }

        const newMap = new Map(prev);

        // Remove old temp conversation
        newMap.delete(tempId);

        // Add conversation with real ID, preserving latest history and metadata
        newMap.set(realId, {
          ...tempConversation,
          id: realId,
          isPending: false, // No longer pending
        });

        return newMap;
      });

      // Update active conversation ID if it was the temp one
      if (activeConversationId === tempId) {
        setActiveConversationId(realId);
      }
    },
    [activeConversationId]
  );

  const updateConversationMeta = useCallback(
    async (
      id: string,
      payload: Partial<
        Pick<
          Conversation,
          | "conversationHistory"
          | "title"
          | "contextFiles"
          | "isPinned"
          | "pageUrl"
        >
      >
    ) => {
      const existing = conversations.get(id);

      // For pending conversations, only update local state (no backend yet)
      if (existing?.isPending) {
        setConversations(prev => {
          const newMap = new Map(prev);
          const current = newMap.get(id);
          if (!current) {
            return newMap;
          }
          newMap.set(id, {
            ...current,
            ...payload,
            updatedAt: Date.now(),
          });
          return newMap;
        });
        return;
      }

      const updated = await updateConversationMutation.mutateAsync({
        id,
        payload,
      });

      setConversations(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(id);
        newMap.set(id, {
          ...(current ?? updated),
          ...updated,
        });
        return newMap;
      });
    },
    [conversations, updateConversationMutation]
  );

  // Delete conversation
  const deleteConversation = useCallback(
    async (id: string) => {
      const conv = conversations.get(id);

      // If it's a pending conversation, just remove from local state
      if (conv?.isPending) {
        setConversations(prev => {
          const newMap = new Map(prev);
          newMap.delete(id);
          return newMap;
        });

        // If deleted conversation was active, switch to another or null
        if (activeConversationId === id) {
          const remaining = Array.from(conversations.values()).filter(
            c => c.id !== id
          );
          if (remaining.length > 0) {
            setActiveConversationId(remaining[0].id);
          } else {
            setActiveConversationId(null);
          }
        }
        return;
      }

      // For saved conversations, call API with retry logic
      let retries = 3;
      let lastError: Error | null = null;

      while (retries > 0) {
        try {
          await deleteConversationMutation.mutateAsync(id);

          // Remove from local state
          setConversations(prev => {
            const newMap = new Map(prev);
            newMap.delete(id);
            return newMap;
          });

          // If deleted conversation was active, switch to another or null
          if (activeConversationId === id) {
            const remaining = Array.from(conversations.values()).filter(
              c => c.id !== id
            );
            if (remaining.length > 0) {
              setActiveConversationId(remaining[0].id);
            } else {
              setActiveConversationId(null);
            }
          }
          return; // Success
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));
          retries--;

          // If network error and retries remaining, wait before retry
          if (retries > 0) {
            await new Promise(resolve =>
              setTimeout(resolve, 1000 * (4 - retries))
            ); // Exponential backoff
          }
        }
      }

      // All retries failed
      console.error("Failed to delete conversation after retries:", lastError);
      throw lastError || new Error("Failed to delete conversation");
    },
    [conversations, activeConversationId, deleteConversationMutation]
  );

  return {
    conversations,
    activeConversationId,
    activeConversation,
    setActiveConversationId,
    createNewConversation,
    updateConversationHistory,
    appendUserMessage,
    appendAssistantMessage,
    appendMessage,
    clearConversationHistory,
    updateConversationMeta,
    replaceConversationId,
    deleteConversation,
    isLoading: isLoadingFromApi,
    error: apiError ? "Failed to load conversations" : null,
    total: apiPage?.total ?? 0,
    pagedConversations: apiPage?.data ?? [],
  };
}
